import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const FeatureFlagsContext = createContext({});

/** Temporary product hide. Flip to true to restore Templates UI + tour step. */
export const TEMPLATES_FEATURE_ENABLED = false;

// All flags default to false for new users
const DEFAULT_FLAGS = {
  templates: TEMPLATES_FEATURE_ENABLED,
};

// Helper to get storage key unique per user
const getStorageKey = (userId) => `feature_flags_${userId}`;

export const FeatureFlagsProvider = ({ children }) => {
  const { currentUser } = useAuth(); // user object from AuthContext
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  // Reset / load flags whenever the authenticated user changes
  useEffect(() => {
    const userId = currentUser?.userId || currentUser?.id || currentUser?.email;
    if (!userId) {
      // No user -> reset to default false
      setFlags(DEFAULT_FLAGS);
      setLoading(false);
      return;
    }

    // Try to load previously saved flags for this user (if any)
    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setFlags(parsed);
      } catch (err) {
        console.error('Failed to parse feature flags', err);
        setFlags(DEFAULT_FLAGS);
      }
    } else {
      // New user → default flags (all false)
      setFlags(DEFAULT_FLAGS);
    }
    setLoading(false);
  }, [currentUser]); // re-run when user changes (login, logout, signup)

  const isEnabled = useCallback((key) => Boolean(flags[key]), [flags]);

  const updateFeature = useCallback((key, value) => {
    setFlags((prev) => {
      const newFlags = { ...prev, [key]: value };
      // Persist to localStorage for this user
      const userId = currentUser?.userId || currentUser?.id || currentUser?.email;
      if (userId) {
        const storageKey = getStorageKey(userId);
        localStorage.setItem(storageKey, JSON.stringify(newFlags));
      }
      return newFlags;
    });
  }, [currentUser]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isEnabled, loading, updateFeature }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);